<template>
  <div id="gatewayDataUp">
    <card :title="$t('g-dataUp:filter')" :visible="false" collapsible>
      <div slot="card-body">
        <div class="form-row">
          <div class="form-group col-sm-6">
            <label for="period" class="control-label">{{ $t('g-dataUp:period') | capitalize }}</label>
            <div class="input-group">
              <flat-pickr id="period"
                          v-model="filterPeriod"
                          :placeholder="$t('g-dataUp:periodPlaceholder') | capitalize"
                          :config="fpConfig"
                          class="form-control"
              />
              <div class="input-group-append">
                <button id="resetPeriodButton" class="btn btn-flat btn-sm" type="button" @click="filterPeriod=null"><i class="icon-remove" /></button>
              </div>
            </div>
          </div>

          <div class="form-group col-sm-6">
            <label for="filterDataRate" class="control-label">{{ $t('g-dataUp:dataRate') | capitalize }}</label>
            <input id="filterDataRate"
                   v-model="filterDataRate"
                   v-validate="'text'"
                   :data-vv-as="$t('g-dataUp:dataRate')"
                   :class="{ 'is-invalid': errors.has('filterDataRate') }"
                   name="filterDataRate"
                   class="form-control"
                   type="text"
            >
            <div class="invalid-feedback">{{ errors.first('filterDataRate') }}</div>
          </div>
        </div>

        <h5>{{ $t('g-dataUp:frequency') | capitalize }}</h5>

        <div class="form-row">
          <div class="form-group col-sm-6">
            <label for="frequencyFrom" class="control-label">{{ $t('g-dataUp:from') | capitalize }}</label>
            <input id="frequencyFrom"
                   v-model="filterFreqFrom"
                   v-validate="'number|between:0,1000'"
                   :data-vv-as="$t('g-dataUp:from')"
                   :class="{ 'is-invalid': errors.has('frequencyFrom') }"
                   name="frequencyFrom"
                   class="form-control"
                   type="text"
                   min="0"
                   max="1000"
            >
            <div class="invalid-feedback">{{ errors.first('frequencyFrom') }}</div>
          </div>

          <div class="form-group col-sm-6">
            <label for="frequencyTo" class="control-label">{{ $t('g-dataUp:to') | capitalize }}</label>
            <input id="frequencyTo"
                   v-model="filterFreqTo"
                   v-validate="'number|between:0,1000'"
                   :data-vv-as="$t('g-dataUp:to')"
                   :class="{ 'is-invalid': errors.has('frequencyTo') }"
                   name="frequencyTo"
                   class="form-control"
                   type="text"
                   min="0"
                   max="1000"
            >
            <div class="invalid-feedback">{{ errors.first('frequencyTo') }}</div>
          </div>
        </div>
      </div>

      <div slot="card-footer">
        <button id="filterButton" class="btn btn-primary" @click="filter()">{{ $t('g-dataUp:search') | capitalize }}</button>
      </div>
    </card>

    <grid id="gatewayDataUpGrid"
          :title="$t('g-dataUp:dataUp')"
          :columns="columns"
          :paginated-dto="rows"
          :wrap-header="true"
          :enable-click-on-row="true"
          :columns-visibility-btn="true"
          @clickOnRow="displayDetailsModal"
          @load="filter"
    >
      <template slot="recvTime" slot-scope="props">{{ props.dto.recvTime | date }}</template>

      <template slot="devEui" slot-scope="props">
        <router-link :to="{name: 'endDevice', params: {devEui: props.dto.endDevice.devEui}}">{{ props.dto.endDevice.devEui }}</router-link>
      </template>

      <template slot="radioId" slot-scope="props">
        <div v-for="(gateway, indexGateway) in props.dto.gwInfo" :key="indexGateway" class="multiple-data-cell border-top">{{ gateway.radioId }}</div>
      </template>

      <template slot="channel" slot-scope="props">
        <div v-for="(gateway, indexGateway) in props.dto.gwInfo" :key="indexGateway" class="multiple-data-cell border-top">{{ gateway.channel }}</div>
      </template>

      <template slot="rssi" slot-scope="props">
        <div v-for="(gateway, indexGateway) in props.dto.gwInfo" :key="indexGateway" class="multiple-data-cell border-top">{{ gateway.rssi | number('0,0.[00]','',' dBm') }}</div>
      </template>

      <template slot="snr" slot-scope="props">
        <div v-for="(gateway, indexGateway) in props.dto.gwInfo" :key="indexGateway" class="multiple-data-cell border-top">{{ gateway.snr | number('0,0.[00]','',' dB') }}</div>
      </template>

      <template slot="fineTimestamp" slot-scope="props">
        <div v-for="(gateway, indexGateway) in props.dto.gwInfo" :key="indexGateway" class="multiple-data-cell border-top">
          <template>{{ gateway.fineTimestamp || '-' }}</template>
        </div>
      </template>
    </grid>

    <modal id="detailsModal"
           v-model="showDetailsModal"
           :title="detailsModalTitle"
    >
      <div slot="modal-body">
        <div class="row">
          <label class="col-md-3">{{ $t('g-dataUp:status') | capitalize }}</label>
          <div class="col-md-9">{{ detailsModalBody.status }}</div>
        </div>
        <div class="row">
          <label class="col-md-3">{{ $t('g-dataUp:encodingType') | capitalize }}</label>
          <div class="col-md-9">{{ $t('g-dataUp:' + detailsModalBody.encodingType) | capitalize }}</div>
        </div>
        <div class="row">
          <label class="col-md-3">{{ $t('g-dataUp:payload') | capitalize }}</label>
          <div class="col-md-9 payload">{{ detailsModalBody.payload }}</div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { upperFirst, isEmpty, map, filter } from 'lodash-es'
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'

export default {
  titleKey: 'gatewayDataUp',

  props: {
    eui: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      // Filters
      filterDataRate: '',
      filterPeriod: '',
      filterFreqFrom: '',
      filterFreqTo: '',
      // Display details in modal
      showDetailsModal: false,
      detailsModalTitle: '',
      detailsModalBody: {},
      // Display messages
      rows: {},

      // In data to be modified if needed
      fpConfig: {
        wrap: true,
        mode: 'range',
        enableTime: true,
        altInput: true,
        altFormat: 'Y/m/d H:i',
        dateFormat: 'U',
        time_24hr: true
      }
    }
  },

  computed: {
    ...mapGetters(['gateway']),

    columns () {
      return [
        { text: this.$t('g-dataUp:recvTime'), slot: 'recvTime' },
        { text: this.$t('g-dataUp:devEui'), slot: 'devEui' },
        { text: this.$t('g-dataUp:devAddr'), formatter: message => message.endDevice ? message.endDevice.devAddr : '' },
        { text: this.$t('g-dataUp:ulFrequency'), formatter: message => message.ulFrequency + ' MHz' },
        { text: this.$t('g-dataUp:rssi'), slot: 'rssi', class: 'p-0' },
        { text: this.$t('g-dataUp:snr'), slot: 'snr', class: 'p-0' },
        { text: this.$t('g-dataUp:dataRate'), formatter: message => message.dataRate },
        { text: this.$t('g-dataUp:radioId'), slot: 'radioId', class: 'p-0' },
        { text: this.$t('g-dataUp:channel'), slot: 'channel', class: 'p-0' },
        { text: this.$t('g-dataUp:codingRate'), formatter: message => message.codingRate },
        { text: this.$t('g-dataUp:modulation'), formatter: message => message.modulation },
        { text: this.$t('g-dataUp:fineTimestamp'), slot: 'fineTimestamp', class: 'p-0' },
        { text: this.$t('g-dataUp:delayed'), formatter: message => upperFirst(message.delayed ? this.$t('ed-dataUp:yes') : this.$t('ed-dataUp:no')) }
      ]
    }
  },

  watch: {
    eui () {
      // Reset data, and get data from new gateway
      this.rows = {}
    },

    gateway () {
      // Do not refresh is we olready have the data
      if (!isEmpty(this.rows)) return

      this.filter()
    }
  },

  created () {
    this.filter()
  },

  methods: {
    async filter (options = {}) {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      let search = []

      // Add selected gateway
      if (!isEmpty(this.gateway)) {
        search = this.$utils.addSearch(search, 'eq', this.gateway.eui, 'gwInfo.gwEui')
      }

      // Format criteria
      // Flatpickr return a string "<from> to <to>" with <from> and <to> dates in seconds
      const filterPeriod = this.filterPeriod ? this.filterPeriod.split(' to ').map(v => v * 1000) : []
      if (!isEmpty(filterPeriod)) {
        search = this.$utils.addSearch(search, 'gte', filterPeriod[0], 'recvTime')
        search = this.$utils.addSearch(search, 'lte', filterPeriod[1], 'recvTime')
      }
      if (this.filterDataRate) search = this.$utils.addSearch(search, 'eq', this.filterDataRate, 'dataRate')
      if (this.filterFreqFrom) search = this.$utils.addSearch(search, 'gte', Number(this.filterFreqFrom), 'ulFrequency')
      if (this.filterFreqTo) search = this.$utils.addSearch(search, 'lte', Number(this.filterFreqTo), 'ulFrequency')

      if (search.length > 0) options.search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))

      // Get filtered data
      this.getDataUp(options)
    },

    async getDataUp (options) {
      if (isEmpty(this.gateway)) return

      const params = this.$utils.getParams({ sort: '-recvTime' }, options, this.queryPageParams)
      let error, response
      [error, response] = await to(this.$api.getGatewayDataUp(this.$store.getters.application, params))
      if (error || !response) return

      // Filter to display only Data Up for this gateway
      response.data.list = map(response.data.list, dataUp => {
        dataUp.gwInfo = filter(dataUp.gwInfo, gw => gw.gwEui === this.eui)
        return dataUp
      })
      this.rows = response.data
    },

    displayDetailsModal (dto) {
      this.detailsModalTitle = upperFirst(this.$t('g-dataUp:dataDetails', { message: dto.msgId }))
      this.detailsModalBody = {
        status: upperFirst(dto.encrypted ? this.$t('g-dataUp:encrypted') : this.$t('g-dataUp:plain')),
        encodingType: dto.encodingType,
        payload: dto.payload
      }
      this.showDetailsModal = true
    }
  }
}
</script>
